import { Footer } from '@/features/landing/sections/Footer'
import { Nav } from '@/features/landing/sections/Nav'
import { ClosingCTA } from '@/features/marketing/components/ClosingCTA'
import { FeeComparison } from '@/features/marketing/components/FeeComparison'
import { PricingCalculator } from '@/features/marketing/components/PricingCalculator'
import { PricingTierCards } from '@/features/marketing/components/PricingTierCards'

/**
 * /pricing — public pricing page.
 *
 * Structure:
 *   1. Hero (tagline)
 *   2. Tier cards (monthly/annual toggle)
 *   3. ROI calculator (interactive)
 *   4. Comparison table (per-feature, tier-by-tier)
 *   5. Trust strip (Stripe, refunds, billing transparency)
 *   6. FAQ
 *   7. Closing CTA
 *
 * Pricing was originally on the "defer to v2" list but bumped into v1
 * (per the §10 override in MARKETING_EXPLORE_PLAN.md) because the #1
 * owner reservation is "what does it cost / what do I get back". The
 * calculator turns that into a one-screen answer.
 */
export default function ExplorePricing() {
  return (
    <div className="min-h-dvh bg-bg text-fg">
      <Nav />
      <main>
        {/* Hero */}
        <section className="border-b border-border/60 px-6 pt-20 pb-12 text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border bg-surface/80 px-3 py-1 font-display text-xs tracking-wider text-fg-muted uppercase">
            <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            Pricing
          </div>
          <h1 className="font-display mx-auto mt-7 max-w-3xl text-balance text-5xl leading-[1.05] font-bold md:text-6xl lg:text-7xl">
            One squadron. One subscription. No per-member fees.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-pretty text-base text-fg-muted md:text-lg">
            Start free. Upgrade when your squad outgrows 10 members or you
            want paid prize pools. No card to start, no auto-upgrade trap,
            no seat tax.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-pretty text-sm text-fg-subtle md:text-base">
            e404 is six weeks old. The free tier is the full product at 10
            members, and the founding squads help shape what ships next.
          </p>
        </section>

        {/* Tier cards */}
        <section className="border-b border-border/40 px-6 py-16">
          <div className="mx-auto max-w-6xl">
            <PricingTierCards />
          </div>
        </section>

        {/* Calculator */}
        <section className="border-b border-border/40 bg-surface/30 px-6 py-20">
          <div className="mx-auto max-w-5xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="font-display text-xs font-bold tracking-[0.3em] text-primary uppercase">
                "Will this actually pay for itself?"
              </p>
              <h2 className="font-display mt-4 text-balance text-3xl leading-tight font-bold md:text-4xl">
                Move the sliders. See your numbers.
              </h2>
              <p className="mt-4 text-pretty text-sm text-fg-muted md:text-base">
                The maths the spreadsheets never quite agree on: gross
                fees in, Stripe out, e404 tier out, what you keep. No
                signup, no email gate — just play with it.
              </p>
            </div>
            <div className="mt-10">
              <PricingCalculator />
            </div>
            <div className="mx-auto mt-10 max-w-2xl">
              <FeeComparison />
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="border-b border-border/40 px-6 py-20">
          <div className="mx-auto max-w-5xl">
            <h2 className="font-display text-center text-3xl font-bold md:text-4xl">
              What's in each tier
            </h2>
            <p className="mt-3 text-center text-sm text-fg-muted">
              Everything in FREE works the same in every tier. Higher
              tiers add capacity and the heavier features.
            </p>
            <div className="mt-10 overflow-hidden rounded-xl border border-border">
              <ComparisonTable />
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="border-b border-border/40 px-6 py-16">
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            <TrustItem
              title="Billed via Stripe"
              body="No card stored by e404. Cancel anytime in your dashboard — settings flip immediately, no support ticket dance."
            />
            <TrustItem
              title="No per-member fee"
              body="Flat subscription per squadron. Add another 40 members tomorrow; tier price doesn't budge."
            />
            <TrustItem
              title="Only 1% on prize pools"
              body="Squadron subs are flat. The only variable cost is a 1% platform fee on paid tournament pots — the rest is yours."
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="border-b border-border/40 bg-surface/30 px-6 py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-center text-3xl font-bold md:text-4xl">
              Frequently asked
            </h2>
            <div className="mt-10 space-y-4">
              <FaqItem
                q="What happens at the 10-member cap on FREE?"
                a="The 11th join request sits as PENDING until you upgrade or remove an existing member. No surprise charge, no auto-upgrade. We just don't let the roster grow past the cap."
              />
              <FaqItem
                q="Can I take member fees on FREE?"
                a="No — paid member subscriptions need Stripe Connect onboarding, which is part of BRONZE and up. FREE is for casual squads where you don't charge."
              />
              <FaqItem
                q="What does the 1% prize-pool fee mean?"
                a="When you run a tournament with paid entry, we take 1% of the prize pool. Stripe's standard processing fees apply on top. The 1% is the lowest in the market — most platforms take 5-10%."
              />
              <FaqItem
                q="Can I downgrade if my squadron shrinks?"
                a="Yes. Downgrade gives you a pro-rated credit on the next invoice. You can't downgrade below your current member count, though — eject members first."
              />
              <FaqItem
                q="What about cancellation?"
                a="Cancel from settings anytime. Your squadron stays at the paid tier until the end of the billing period you've already paid for, then drops to FREE. Members and history are preserved."
              />
              <FaqItem
                q="Is there a free trial?"
                a="The FREE tier is the trial. It's the full product at 10 members — not a stripped-down version. When you need 11+ members or paid prize pools, upgrade then."
              />
            </div>
          </div>
        </section>

        <ClosingCTA
          location="pricing"
          headline="Start free. Decide later."
          subline="No card, no email gate. Run a 10-person squadron for as long as you like."
          primaryCtaLabel="Get started free"
          primaryCtaTo="/signup"
        />
      </main>
      <Footer />
    </div>
  )
}

/**
 * Per-feature comparison matrix. Manually authored — keeping it
 * small enough that this stays accurate; if it gets longer, source
 * it from a config file alongside `tierMeta.ts`.
 */
function ComparisonTable() {
  const rows: Array<{
    feature: string
    free: string | boolean
    bronze: string | boolean
    silver: string | boolean
    gold: string | boolean
  }> = [
    // Governed by contracts/TIER_CAPABILITIES.md (golden). Every row is a
    // real, granted capability — no phantoms, no aspirational gates.
    { feature: 'Member cap', free: '10', bronze: '25', silver: '50', gold: '300' },
    { feature: 'Admin seats', free: '1', bronze: '3', silver: '5', gold: '10' },
    { feature: 'Roster, availability & events', free: true, bronze: true, silver: true, gold: true },
    { feature: 'Discord bot + event sync', free: true, bronze: true, silver: true, gold: true },
    { feature: 'Twitch live status', free: true, bronze: true, silver: true, gold: true },
    { feature: 'Verified org status', free: true, bronze: true, silver: true, gold: true },
    { feature: 'Monthly squadron report (PDF)', free: '1 / mo', bronze: '2 / mo', silver: '2 / mo', gold: '2 / mo' },
    { feature: 'Public events / month', free: '0', bronze: '1', silver: '4', gold: '10' },
    { feature: 'Tournaments — max players', free: false, bronze: '25', silver: '50', gold: '300' },
    { feature: 'Stream events', free: false, bronze: true, silver: true, gold: true },
    { feature: 'Custom squadron logo', free: false, bronze: true, silver: true, gold: true },
    { feature: 'Member fee collection (Stripe)', free: false, bronze: true, silver: true, gold: true },
    { feature: 'Revenue dashboard', free: false, bronze: true, silver: true, gold: true },
    { feature: 'Tournament Stadium (TV view)', free: false, bronze: true, silver: true, gold: true },
    { feature: 'Cross-squadron tournaments (soon)', free: false, bronze: false, silver: true, gold: true },
    { feature: 'Paid prize-pool tournaments', free: false, bronze: false, silver: false, gold: true },
    { feature: 'Membership tiers (rewards designer)', free: false, bronze: false, silver: false, gold: true },
    { feature: 'Priority support', free: false, bronze: false, silver: false, gold: true },
  ]

  const cell = (v: string | boolean) => {
    if (v === true) return <span className="text-success" aria-label="included">✓</span>
    if (v === false) return <span className="text-fg-subtle/40" aria-label="not included">—</span>
    return <span className="font-display text-xs font-bold tabular-nums text-fg">{v}</span>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left">
        <thead className="bg-bg/40">
          <tr>
            <th className="font-display border-b border-border px-4 py-3 text-[10px] font-extrabold tracking-[0.22em] text-fg-subtle uppercase">
              Feature
            </th>
            <th className="font-display border-b border-border px-4 py-3 text-center text-[10px] font-extrabold tracking-[0.22em] text-fg-subtle uppercase">
              FREE
            </th>
            <th
              className="font-display border-b border-border px-4 py-3 text-center text-[10px] font-extrabold tracking-[0.22em] uppercase"
              style={{ color: '#CD7F32' }}
            >
              BRONZE
            </th>
            <th
              className="font-display border-b border-border px-4 py-3 text-center text-[10px] font-extrabold tracking-[0.22em] uppercase"
              style={{ color: '#C0C5D6' }}
            >
              SILVER
            </th>
            <th
              className="font-display border-b border-border px-4 py-3 text-center text-[10px] font-extrabold tracking-[0.22em] uppercase"
              style={{ color: '#FFC857' }}
            >
              GOLD
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.feature} className={i % 2 === 0 ? 'bg-surface/20' : ''}>
              <td className="px-4 py-2.5 text-xs text-fg">{r.feature}</td>
              <td className="px-4 py-2.5 text-center text-sm">{cell(r.free)}</td>
              <td className="px-4 py-2.5 text-center text-sm">{cell(r.bronze)}</td>
              <td className="px-4 py-2.5 text-center text-sm">{cell(r.silver)}</td>
              <td className="px-4 py-2.5 text-center text-sm">{cell(r.gold)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TrustItem({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-border bg-bg/40 p-5">
      <p className="font-display text-[10px] font-extrabold tracking-[0.22em] text-primary uppercase">
        {title}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-fg-muted">{body}</p>
    </div>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="group rounded-xl border border-border bg-bg/40 px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
      <summary className="font-display flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-fg">
        {q}
        <span
          aria-hidden
          className="font-display text-lg text-fg-subtle transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="mt-3 text-xs leading-relaxed text-fg-muted">{a}</p>
    </details>
  )
}
